import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldErrors,
} from 'react-hook-form';
import { createVacationFormSchema, type CreateVacationFormValues } from '@/schemas/absence';
import { absenceService } from '@/api/domains/absence/absence.service';
import { ABSENCE_SCOPE_LIST_REFRESH_EVENT } from '@/api/domains/absence/absence.types';
import type { LC_AbsenceWorkdayTypeItemOut } from '@/api/domains/absence/absence.types';
import { useUI } from '@/hooks/ui';
import { useSidePanelLoading } from '@/hooks/ui/useSidePanelLoading';
import { useDebounce } from '@/hooks/ui/useDebounce';
import { useAbsenceEmployeeSelector } from '@/hooks/absence/useAbsenceEmployeeSelector';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { normalizeNumericSelectValue } from '@/utils/formHelpers';
import { injectValidationErrors } from '@/utils';


const CONFLICT_DATE_OVERLAP_CODE = 'CONFLICT_DATE_OVERLAP';
const CONFLICT_EMPLOYEE_BLOCKED_CODE = 'CONFLICT_EMPLOYEE_BLOCKED';
const CALCULATED_DATES_DEBOUNCE_MS = 400;

export interface UseCreateVacationResult {
  // Form
  form: {
    register: UseFormRegister<CreateVacationFormValues>;
    setValue: UseFormSetValue<CreateVacationFormValues>;
    watch: UseFormWatch<CreateVacationFormValues>;
    errors: FieldErrors<CreateVacationFormValues>;
    trigger: UseFormTrigger<CreateVacationFormValues>;
  };
  isSubmitted: boolean;
  isSubmitting: boolean;
  handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;

  // Employee selector
  employeeOptions: SelectorOption[];
  hasBlockedEmployees: boolean;
  isLoadingEmployees: boolean;
  isLoadingMoreEmployees: boolean;
  employeeHasMore: boolean;
  handleEmployeeSearch: (search: string) => void;
  handleEmployeeChange: (value: string | string[]) => void;
  loadMoreEmployees: () => Promise<void>;

  // Workday types
  workdayTypeOptions: SelectorOption[];
  isLoadingWorkdayTypes: boolean;

  /**
   * True mientras no haya un colaborador válido seleccionado: el resto del formulario
   * queda bloqueado.
   */
  isEmployeeSelectionPending: boolean;
}

export function useCreateVacation(): UseCreateVacationResult {
  const { t: translate } = useTranslation('absence');
  const { showApiError, showSuccess, showNotification, closeSidePanel } = useUI();
  const { setLoading } = useSidePanelLoading();

  // ── Workday types ──────────────────────────────────────────────────────────
  // Sin AbortController: es un catálogo estático de una sola pasada y abortarlo en el cleanup
  // generaría TaskCanceledException en el backend con los doble-montajes de React.StrictMode.
  const [workdayTypes, setWorkdayTypes] = useState<LC_AbsenceWorkdayTypeItemOut[]>([]);
  const [isLoadingWorkdayTypes, setIsLoadingWorkdayTypes] = useState(false);

  const loadWorkdayTypes = useCallback(async () => {
    setIsLoadingWorkdayTypes(true);
    try {
      const response = await absenceService.getAbsenceWorkdayTypes();
      if (response.success && response.data) {
        setWorkdayTypes(response.data);
      } else {
        showApiError(response, translate('vacation.errors.loadWorkdayTypesFailed'));
      }
    } finally {
      setIsLoadingWorkdayTypes(false);
    }
  }, [showApiError, translate]);

  useEffect(() => {
    void loadWorkdayTypes();
  }, [loadWorkdayTypes]);

  const workdayTypeOptions = useMemo<SelectorOption[]>(
    () => workdayTypes.map((type) => ({ value: type.code, label: type.label })),
    [workdayTypes],
  );

  // ── Employee selector ──────────────────────────────────────────────────────
  // Mismo selector que licencia médica, incluido el bloqueo de colaboradores con contrato
  // sincronizado con Rex Remuneraciones.
  const {
    employeeOptions,
    hasBlockedEmployees,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    loadMoreEmployees,
    selectEmployee,
  } = useAbsenceEmployeeSelector({
    loadErrorMessage: translate('vacation.errors.loadEmployeesFailed'),
    blockedTooltip: translate('vacation.form.blockedEmployeeTooltip'),
  });

  // ── Loading state ──────────────────────────────────────────────────────────
  const initialLoadDoneRef = useRef(false);
  const areCatalogsLoaded = useMemo(
    () => !isLoadingEmployees && !isLoadingWorkdayTypes,
    [isLoadingEmployees, isLoadingWorkdayTypes],
  );

  useEffect(() => {
    if (!initialLoadDoneRef.current) {
      setLoading(!areCatalogsLoaded);
      if (areCatalogsLoaded) {
        initialLoadDoneRef.current = true;
      }
    }
  }, [areCatalogsLoaded, setLoading]);

  // ── Form ───────────────────────────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const overlapErrorBaselineRef = useRef<
    { startDate: string; dayCount: number | null } | null
  >(null);

  const {
    register,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
    handleSubmit: rhfHandleSubmit,
    formState,
  } = useForm<CreateVacationFormValues>({
    resolver: zodResolver(createVacationFormSchema),
    mode: 'onSubmit',
    defaultValues: {
      employeeTargetId: null,
      workdayType: undefined as unknown as string,
      dayCount: null,
      startDate: '',
      endDate: '',
      returnDate: '',
      detail: '',
    },
  });

  // ── Fechas calculadas ──────────────────────────────────────────────────────
  // A diferencia de licencia médica y permiso especial, la fecha de término de vacaciones NO es
  // aritmética de días corridos: cuenta solo días hábiles, por lo que depende de los feriados de la
  // empresa y del colaborador. Se resuelve en el backend, que devuelve término y retorno juntos.
  const calculatedDatesRequestIdRef = useRef(0);

  const employeeTargetId = watch('employeeTargetId');
  const startDate = watch('startDate');
  const dayCount = watch('dayCount');
  const workdayType = watch('workdayType');

  // Los cuatro insumos comparten el mismo retardo: así una edición que toca varios campos dispara
  // una sola petición, y ninguna sale con una combinación a medio actualizar.
  const debouncedEmployeeTargetId = useDebounce(employeeTargetId, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedStartDate = useDebounce(startDate, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedDayCount = useDebounce(dayCount, CALCULATED_DATES_DEBOUNCE_MS);
  const debouncedWorkdayType = useDebounce(workdayType, CALCULATED_DATES_DEBOUNCE_MS);

  const calculateDates = useCallback(
    async (params: {
      employeeTargetId: number;
      startDate: string;
      dayCount: number;
      workdayType: string;
    }) => {
      const requestId = ++calculatedDatesRequestIdRef.current;
      const response = await absenceService.calculateVacationDates(params);
      if (requestId !== calculatedDatesRequestIdRef.current) return; // respuesta obsoleta, ignorar
      if (response.success && response.data) {
        setValue('endDate', response.data.endDate, { shouldDirty: true });
        setValue('returnDate', response.data.returnDate, { shouldDirty: true });
      } else {
        // Sin esto, las fechas del cálculo anterior sobreviven al error y, como el esquema
        // solo exige que no estén vacías, Guardar quedaría habilitado con un período que ya
        // no corresponde a los insumos actuales.
        setValue('endDate', '');
        setValue('returnDate', '');
        showApiError(response, translate('vacation.errors.calculateDatesFailed'));
      }
    },
    [setValue, showApiError, translate],
  );

  useEffect(() => {
    const numericDayCount = Number(debouncedDayCount);
    if (
      !debouncedEmployeeTargetId ||
      !debouncedStartDate ||
      !debouncedWorkdayType ||
      !Number.isInteger(numericDayCount) ||
      numericDayCount <= 0
    ) {
      setValue('endDate', '');
      setValue('returnDate', '');
      return;
    }
    void calculateDates({
      employeeTargetId: debouncedEmployeeTargetId as number,
      startDate: debouncedStartDate,
      dayCount: numericDayCount,
      workdayType: debouncedWorkdayType,
    });
  }, [
    debouncedEmployeeTargetId,
    debouncedStartDate,
    debouncedDayCount,
    debouncedWorkdayType,
    calculateDates,
    setValue,
  ]);

  // Si el usuario cambia el período tras un error de traslape, se limpia para evitar confusión.
  // El baseline guarda el dayCount ya parseado por zod (número), mientras que el valor observado
  // del input llega como string: hay que normalizar antes de comparar o el error se limpiaría
  // en el primer render posterior al submit.
  useEffect(() => {
    const baseline = overlapErrorBaselineRef.current;
    if (!baseline) return;
    const rawDayCount: unknown = dayCount;
    const currentDayCount = rawDayCount === null || rawDayCount === undefined || rawDayCount === ''
      ? null
      : Number(rawDayCount);
    if (baseline.startDate !== startDate || baseline.dayCount !== currentDayCount) {
      overlapErrorBaselineRef.current = null;
      clearErrors(['startDate', 'endDate']);
    }
  }, [startDate, dayCount, clearErrors]);

  const onValid = useCallback(
    async (data: CreateVacationFormValues) => {
      setIsSubmitting(true);
      try {
        // endDate y returnDate no se envían: el servidor los recalcula de forma autoritativa.
        const response = await absenceService.createVacation({
          employeeTargetId: data.employeeTargetId as number,
          startDate: data.startDate,
          workdayType: data.workdayType,
          dayCount: data.dayCount as number,
          detail: data.detail || undefined,
        });

        if (response.success) {
          showSuccess(translate('vacation.messages.createSuccess'));
          window.dispatchEvent(new CustomEvent(ABSENCE_SCOPE_LIST_REFRESH_EVENT));
          closeSidePanel?.();
        } else if (response.error?.code === CONFLICT_DATE_OVERLAP_CODE) {
          overlapErrorBaselineRef.current = { startDate: data.startDate, dayCount: data.dayCount };
          setError('startDate', { message: 'absence:vacation.errors.dateOverlap' });
          setError('endDate', { message: 'absence:vacation.errors.dateOverlap' });
          showNotification('error', translate('vacation.errors.dateOverlap'));
        } else if (response.error?.code === CONFLICT_EMPLOYEE_BLOCKED_CODE) {
          showNotification('error', translate('vacation.form.blockedEmployeeTooltip'));
        } else if (response.error?.validationErrors && response.error.validationErrors.length > 0) {
          injectValidationErrors(response.error.validationErrors, { setError });
        } else {
          showApiError(response, translate('vacation.errors.createFailed'));
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [closeSidePanel, setError, showApiError, showNotification, showSuccess, translate],
  );

  const handleEmployeeChange = useCallback(
    (value: string | string[]) => {
      const stringValue = Array.isArray(value) ? (value[0] ?? '') : value;
      const numericId = normalizeNumericSelectValue(stringValue);
      if (numericId !== undefined) {
        setValue('employeeTargetId', numericId as number, { shouldDirty: true });
        selectEmployee(numericId as number);
      }
    },
    [selectEmployee, setValue],
  );

  const handleSubmit = useCallback(
    (event?: React.FormEvent<HTMLFormElement>) => {
      if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
        event.stopPropagation();
      }
      const wrappedValid = (data: CreateVacationFormValues) => { void onValid(data); };
      const wrappedInvalid = () => {};
      const runner = rhfHandleSubmit(wrappedValid, wrappedInvalid);
      void runner(event);
    },
    [rhfHandleSubmit, onValid],
  );

  return {
    form: {
      register,
      setValue,
      watch,
      errors: formState.errors,
      trigger,
    },
    isSubmitted: formState.isSubmitted,
    isSubmitting,
    handleSubmit,
    employeeOptions,
    hasBlockedEmployees,
    isLoadingEmployees,
    isLoadingMoreEmployees,
    employeeHasMore,
    handleEmployeeSearch,
    handleEmployeeChange,
    loadMoreEmployees,
    workdayTypeOptions,
    isLoadingWorkdayTypes,
    isEmployeeSelectionPending: !employeeTargetId,
  };
}

export default useCreateVacation;
